import { useEffect, useRef, useState } from 'react';
import {
  UploadSimple, FolderOpen, CheckCircle, XCircle, CircleNotch,
  Circle, CaretDown, CaretRight, Warning,
} from '@phosphor-icons/react';
import { detectSpineGroups, collectDroppedFiles } from '../../lib/spine-detector';
import type { SpineGroup, DetectionResult } from '../../lib/spine-detector';
import { useAssetStore } from '../../stores/assetStore';

type ItemStatus = 'pending' | 'uploading' | 'done' | 'error';

interface ImportItem {
  group: SpineGroup;
  status: ItemStatus;
  error?: string;
}

type Phase = 'idle' | 'scanning' | 'preview' | 'importing' | 'done';

interface SmartAssetImporterProps {
  onImportComplete?: () => void;
  initialFiles?: File[];
}

export default function SmartAssetImporter({ onImportComplete, initialFiles }: SmartAssetImporterProps) {
  const uploadAssetGroup = useAssetStore((s) => s.uploadAssetGroup);

  const [phase, setPhase] = useState<Phase>('idle');
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState<ImportItem[]>([]);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);
  const [scanError, setScanError] = useState('');

  const fileInputRef   = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // ── scanning ─────────────────────────────────────────────────────────────

  async function processFiles(files: File[]) {
    setPhase('scanning');
    setScanError('');
    try {
      const result: DetectionResult = await detectSpineGroups(files);
      if (!result.groups.length) {
        setScanError('No Spine assets found. Expected .json + .atlas/.atlas.txt + .png files.');
        setPhase('idle');
        return;
      }
      setItems(result.groups.map((g) => ({ group: g, status: 'pending' })));
      setUnmatched(result.unmatched.map((f) => f.name));
      setPhase('preview');
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Scan failed');
      setPhase('idle');
    }
  }

  // Auto-process files passed in from parent (e.g. page-level drop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (initialFiles?.length) processFiles(initialFiles); }, []);

  // ── event handlers ────────────────────────────────────────────────────────

  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const files = await collectDroppedFiles(e.dataTransfer);
    if (files.length) processFiles(files);
  }

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) processFiles(files);
  }

  // ── import ────────────────────────────────────────────────────────────────

  async function startImport() {
    setPhase('importing');
    const next = [...items];

    for (let i = 0; i < next.length; i++) {
      next[i] = { ...next[i], status: 'uploading' };
      setItems([...next]);
      try {
        await uploadAssetGroup(next[i].group);
        next[i] = { ...next[i], status: 'done' };
      } catch (err) {
        next[i] = {
          ...next[i],
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        };
      }
      setItems([...next]);
    }

    setPhase('done');
  }

  function reset() {
    setPhase('idle');
    setItems([]);
    setUnmatched([]);
    setScanError('');
    setUnmatchedOpen(false);
  }

  // ── render ────────────────────────────────────────────────────────────────

  const doneCount  = items.filter((i) => i.status === 'done').length;
  const errorCount = items.filter((i) => i.status === 'error').length;

  return (
    <div className="p-5 space-y-4">

      {/* ── Drop zone ── */}
      {(phase === 'idle' || phase === 'scanning') && (
        <>
          <div
            data-testid="smart-import-dropzone"
            className={`relative rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-normal ${
              dragging
                ? 'border-accent bg-accent-subtle'
                : 'border-border-subtle bg-bg-surface hover:border-accent hover:bg-accent-subtle'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {phase === 'scanning' ? (
              <div className="flex flex-col items-center gap-2">
                <CircleNotch size={24} className="text-accent animate-spin" />
                <p className="text-xs font-sans text-text-secondary">Scanning files…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadSimple size={28} className={`${dragging ? 'text-accent' : 'text-text-tertiary'} transition-colors`} />
                <div>
                  <p className="text-sm font-sans font-medium text-text-primary">
                    Drop files or folder here
                  </p>
                  <p className="text-xxs font-sans text-text-tertiary mt-1">
                    .json + .atlas.txt + .png detected automatically
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Browse buttons */}
          {phase === 'idle' && (
            <div className="flex gap-2">
              <button
                data-testid="browse-files-btn"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-sans font-medium rounded-md border border-border bg-bg-surface text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
              >
                <UploadSimple size={13} /> Browse files
              </button>
              <button
                data-testid="browse-folder-btn"
                onClick={() => folderInputRef.current?.click()}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-sans font-medium rounded-md border border-border bg-bg-surface text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
              >
                <FolderOpen size={13} /> Open folder
              </button>
            </div>
          )}

          <input ref={fileInputRef} type="file" multiple accept=".json,.atlas,.atlas.txt,.txt,.skel,.png,image/png" className="hidden" onChange={onFileInputChange} />
          {/* @ts-expect-error – webkitdirectory is non-standard but widely supported */}
          <input ref={folderInputRef} type="file" webkitdirectory="" multiple className="hidden" onChange={onFileInputChange} />

          {scanError && (
            <div className="flex items-start gap-2 rounded-lg bg-error-muted border border-error/20 px-3 py-2.5">
              <Warning size={14} className="text-error shrink-0 mt-0.5" />
              <p className="text-xs font-sans text-error">{scanError}</p>
            </div>
          )}
        </>
      )}

      {/* ── Preview / Importing / Done ── */}
      {(phase === 'preview' || phase === 'importing' || phase === 'done') && (
        <div className="space-y-3" data-testid="smart-import-preview">

          {/* Summary row */}
          <div className="flex items-center justify-between">
            <span className="text-xxs font-mono text-text-tertiary uppercase tracking-widest">
              {items.length} asset{items.length !== 1 ? 's' : ''} detected
            </span>
            {phase === 'importing' && (
              <span className="text-xxs font-mono text-accent">
                {doneCount + errorCount} / {items.length}
              </span>
            )}
            {phase === 'done' && (
              <span className={`text-xxs font-mono ${errorCount > 0 ? 'text-error' : 'text-emerald-400'}`}>
                {errorCount > 0 ? `${errorCount} failed` : 'all done'}
              </span>
            )}
          </div>

          {/* Group list */}
          <div className="rounded-lg border border-border-subtle bg-bg-surface overflow-hidden">
            <ul className="divide-y divide-border-subtle max-h-56 overflow-y-auto">
              {items.map((item, i) => (
                <li
                  key={i}
                  data-testid={`import-item-${item.group.name}`}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    item.status === 'uploading' ? 'bg-accent-subtle' : ''
                  }`}
                >
                  <StatusIcon status={item.status} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-sans font-medium text-text-primary truncate leading-snug">
                      {item.group.name}
                    </p>
                    <p className="text-xxs font-mono text-text-tertiary truncate">
                      {item.group.pngFiles.length} texture{item.group.pngFiles.length !== 1 ? 's' : ''}
                      {item.error && <span className="text-error"> · {item.error}</span>}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Ignored files toggle */}
          {unmatched.length > 0 && (
            <div>
              <button
                className="flex items-center gap-1 text-xxs font-sans text-text-tertiary hover:text-text-secondary transition-colors"
                onClick={() => setUnmatchedOpen((o) => !o)}
              >
                {unmatchedOpen ? <CaretDown size={10} /> : <CaretRight size={10} />}
                {unmatched.length} file{unmatched.length !== 1 ? 's' : ''} ignored
              </button>
              {unmatchedOpen && (
                <ul className="mt-1.5 text-xxs font-mono text-text-tertiary space-y-0.5 pl-3.5 max-h-20 overflow-y-auto border-l border-border-subtle">
                  {unmatched.map((n, i) => <li key={i} className="truncate">{n}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Actions */}
          {phase === 'preview' && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={reset}
                className="flex-1 px-3 py-2 text-xs font-sans font-medium rounded-md border border-border bg-bg-surface text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
              >
                Cancel
              </button>
              <button
                data-testid="import-all-btn"
                onClick={startImport}
                className="flex-[2] px-3 py-2 text-xs font-sans font-medium rounded-md bg-accent text-white hover:bg-accent-hover transition-all duration-fast"
              >
                Import {items.length} asset{items.length !== 1 ? 's' : ''}
              </button>
            </div>
          )}

          {phase === 'done' && (
            <div className="flex gap-2 pt-1">
              <button
                data-testid="import-reset-btn"
                onClick={() => { reset(); }}
                className="flex-1 px-3 py-2 text-xs font-sans font-medium rounded-md border border-border bg-bg-surface text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
              >
                Import more
              </button>
              <button
                onClick={() => { reset(); onImportComplete?.(); }}
                className="flex-[2] px-3 py-2 text-xs font-sans font-medium rounded-md bg-accent text-white hover:bg-accent-hover transition-all duration-fast"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: ItemStatus }) {
  switch (status) {
    case 'pending':   return <Circle       size={14} className="text-text-tertiary mt-0.5 shrink-0" />;
    case 'uploading': return <CircleNotch  size={14} className="text-accent mt-0.5 shrink-0 animate-spin" />;
    case 'done':      return <CheckCircle  size={14} className="text-emerald-400 mt-0.5 shrink-0" />;
    case 'error':     return <XCircle      size={14} className="text-error mt-0.5 shrink-0" />;
  }
}
